package com.qrkontaq.pages.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.qrkontaq.core.auth.Session
import com.qrkontaq.data.dto.MyRestaurantDto
import com.qrkontaq.data.repository.AdminRepository
import com.qrkontaq.di.LocalAppContainer
import com.qrkontaq.ui.admin.AdminBootSplash
import com.qrkontaq.ui.admin.AdminShell
import com.qrkontaq.ui.admin.AdminT
import com.qrkontaq.ui.admin.LoginView
import com.qrkontaq.ui.admin.WizardView
import com.qrkontaq.utils.PageSeo
import com.varabyte.kobweb.core.Page
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

private sealed interface AdminState {
    data object Booting : AdminState

    data object NeedsLogin : AdminState

    /** The session looks fine but the boot request kept failing (flaky network / server hiccup). */
    data object BootFailed : AdminState

    data object NeedsRestaurant : AdminState

    data class Ready(val restaurants: List<MyRestaurantDto>) : AdminState
}

@Page("/admin")
@Composable
fun AdminPage() {
    PageSeo(title = "QRkontaq — Кабінет", description = "")

    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()

    var state by remember { mutableStateOf<AdminState>(AdminState.Booting) }
    var reloadKey by remember { mutableStateOf(0) }

    val adminRepository =
        remember(Session.accessToken) {
            val token = Session.accessToken
            if (token != null) {
                container.createAuthenticatedClients(token)
                AdminRepository(container.getAuthenticatedClient())
            } else {
                null
            }
        }

    LaunchedEffect(reloadKey) {
        // `kobweb export` snapshots the page in a headless browser with no session;
        // staying on the splash keeps the prerendered /admin HTML neutral, so a
        // signed-in refresh never flashes the login card before hydration.
        if (window.navigator.asDynamic().webdriver == true) return@LaunchedEffect
        if (!Session.isLoggedIn || adminRepository == null) {
            state = AdminState.NeedsLogin
            return@LaunchedEffect
        }
        var restaurants: List<MyRestaurantDto>? = null
        for (attempt in 0 until 3) {
            val response = runCatching { adminRepository.myRestaurantsResponse() }.getOrNull()
            if (response?.data != null) {
                restaurants = response.data
                break
            }
            // The Bearer plugin wipes the session only when the refresh token is dead —
            // that is a real logout. Anything else is transient: keep the session and retry.
            if (!Session.isLoggedIn || response?.code == 401 || response?.code == 403) {
                Session.clear()
                state = AdminState.NeedsLogin
                return@LaunchedEffect
            }
            if (attempt < 2) delay(1500)
        }
        state =
            when {
                restaurants == null -> AdminState.BootFailed
                restaurants.isEmpty() -> AdminState.NeedsRestaurant
                else -> AdminState.Ready(restaurants)
            }
    }

    when (val current = state) {
        is AdminState.Booting -> AdminBootSplash()

        is AdminState.BootFailed ->
            AdminBootSplash(
                message = AdminT.bootFailed,
                actionText = AdminT.bootRetry,
                onAction = {
                    state = AdminState.Booting
                    reloadKey++
                },
            )

        is AdminState.NeedsLogin ->
            LoginView { reloadKey++ }

        is AdminState.NeedsRestaurant ->
            adminRepository?.let { repo ->
                WizardView(
                    repository = repo,
                    scope = scope,
                    onCreated = { restaurantId ->
                        Session.activeRestaurantId = restaurantId
                        reloadKey++
                    },
                    // No venue to fall back to: "back" leaves the cabinet for the landing page.
                    onCancel = { window.location.href = "/" },
                )
            }

        is AdminState.Ready ->
            adminRepository?.let { repo ->
                val activeId =
                    Session.activeRestaurantId
                        ?.takeIf { id -> current.restaurants.any { it.restaurant.id == id } }
                        ?: current.restaurants.first().restaurant.id
                AdminShell(
                    repository = repo,
                    scope = scope,
                    restaurants = current.restaurants,
                    activeId = activeId,
                    onLogout = { reloadKey++ },
                    onRestaurantDeleted = {
                        // The active venue is gone: drop the stale selection.
                        Session.activeRestaurantId = null
                        if (current.restaurants.none { it.restaurant.id != activeId }) {
                            // Last venue deleted — remove the whole account too (frees the email
                            // for a fresh sign-up), then sign out to the landing page. The session
                            // is cleared regardless of the request outcome.
                            scope.launch {
                                runCatching { repo.deleteAccount() }
                                Session.clear()
                                window.location.href = "/"
                            }
                        } else {
                            // Other venues remain — re-boot so the list refetches and we land on one.
                            reloadKey++
                        }
                    },
                )
            }
    }
}
